/**
 * Dates RDV — listes et modal Mes commandes (Admin).
 * Source : createdAt / dateDebut / dateFin du contrat API commandes.
 */

import { formatActionDateOnlyFr, formatActionDateTimeFr } from './formatActionDateFr.js';
import { getRdvPaymentView } from './rdvPaymentDisplay.js';

const APP_TIMEZONE = 'Europe/Paris';

function toValidDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

/** Liste : « 13/10/2026 à 17:00 » */
export function formatMesCommandesRdvDateCompact(value) {
  const date = toValidDate(value);
  if (!date) return null;
  const datePart = date.toLocaleDateString('fr-FR', {
    timeZone: APP_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('fr-FR', {
    timeZone: APP_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${datePart} à ${timePart}`;
}

/**
 * @param {object} commande — RDV (type RENDEZ_VOUS)
 * @returns {{ rdvAt: string|null, reservedAt: string|null }}
 */
export function resolveMesCommandesRdvDateSources(commande) {
  const reservedRaw = commande?.createdAt || commande?.dateCommande || null;
  const rdvRaw = commande?.dateDebut || null;
  return {
    rdvAt: formatMesCommandesRdvDateCompact(rdvRaw),
    reservedAt: formatMesCommandesRdvDateCompact(reservedRaw),
  };
}

/** Créneau RDV pour le modal : date + HH:MM → HH:MM */
export function formatRdvAppointmentBlock(dateDebut, dateFin) {
  const start = toValidDate(dateDebut);
  if (!start) {
    return { dateLine: '—', timeLine: null };
  }
  const dateLine = formatActionDateOnlyFr(dateDebut, { includeYear: true }) || '—';
  const end = toValidDate(dateFin);
  if (!end) {
    return { dateLine, timeLine: null };
  }
  const startTime = start.toLocaleTimeString('fr-FR', {
    timeZone: APP_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const endTime = end.toLocaleTimeString('fr-FR', {
    timeZone: APP_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const sameDay =
    start.toLocaleDateString('fr-FR', { timeZone: APP_TIMEZONE }) ===
    end.toLocaleDateString('fr-FR', { timeZone: APP_TIMEZONE });
  const timeLine = sameDay ? `${startTime} → ${endTime}` : `${startTime} → ${endTime} (fin)`;
  return { dateLine, timeLine };
}

export function formatRdvReservedAtLabel(commande) {
  const raw = commande?.createdAt || commande?.dateCommande;
  return formatActionDateTimeFr(raw) || '—';
}

/** Libellé statut paiement RDV (données API existantes). */
export function formatRdvPaymentStatusLabel(commande) {
  const pay = getRdvPaymentView(commande);
  if (!pay) return '—';
  if (pay.isRefunded) return 'Remboursé';
  const ps = String(pay.paymentStatus || '').toUpperCase();
  if (ps === 'AWAITING_PAYMENT') return 'Paiement en attente';
  if (ps === 'PAID' || ps === 'VALIDE') {
    if (pay.amountDueLater > 0.009) return 'Partiellement payé';
    if (pay.amountPaid >= pay.total && pay.total > 0) return 'Payé';
    return 'Payé';
  }
  if (pay.summaryLabel) return pay.summaryLabel;
  return pay.modeLabel || '—';
}

export function buildRdvTimelineEntries(commande) {
  const entries = [];
  const reserved = commande?.createdAt || commande?.dateCommande;
  if (reserved) {
    entries.push({ key: 'reserved', label: 'Réservé le', at: reserved });
  }
  if (commande?.dateDebut) {
    entries.push({ key: 'slot', label: 'RDV prévu le', at: commande.dateDebut });
  }
  if (commande?.dateAnnulation) {
    entries.push({ key: 'cancel', label: 'Annulé le', at: commande.dateAnnulation });
  }
  return entries;
}

export function formatInstructeurDisplay(instructeur, instructeurId) {
  if (instructeur?.prenom || instructeur?.nom) {
    return [instructeur.prenom, instructeur.nom].filter(Boolean).join(' ').trim();
  }
  if (instructeurId) return `Réf. ${String(instructeurId).slice(0, 8)}…`;
  return null;
}
